/** Turn-by-turn steps, derived from the route the API already returns
 *  (item 0110). No graph change, no API change, no extra request: every
 *  ingredient is in `build_route`'s response —
 *
 *    turns          bearing delta across each vertex (smoothed, see LOOK_M)
 *    distances      haversine along each leg
 *    floor changes  segment.transition_out.kind + .to_floor_id
 *    arrival        the last segment's floor
 *
 *  DERIVED ON THE CLIENT, ON PURPOSE. The server owns app/i18n.py and reads
 *  Accept-Language, so it was the other candidate — but the viewer's language
 *  selector changes language WITHOUT refetching anything (lib/i18n.jsx keeps it
 *  in React state and localStorage). Server-rendered sentences would either go
 *  stale the moment a visitor switches language at the totem, or force a route
 *  refetch per switch. So the server stays the source of geometry and facts and
 *  the client stays the source of language, which is the split the rest of the
 *  viewer already uses.
 *
 *  This module returns KEYS AND VARIABLES, never prose. Each step names one
 *  strings.json entry that is a whole grammatical sentence in each of the eight
 *  languages, with {m} / {name} / {floor} interpolated into it. Concatenating
 *  "Gira a la " + direction + " y avanza " + m produces Catalan and French that
 *  nobody notices is broken until a customer does.
 *
 *  Nothing here imports React or i18n, which is what makes it testable on its
 *  own — see the item file for the node run over the real dev routes.
 */

const EARTH_M = 6371008.8;
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

export function haversineM(a, b) {
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Initial bearing a→b, degrees clockwise from north. */
export function bearingDeg(a, b) {
  const φ1 = rad(a[1]);
  const φ2 = rad(b[1]);
  const Δλ = rad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

/** Signed smallest angle from → to, in (-180, 180]. Negative = left. */
const delta = (from, to) => ((((to - from) % 360) + 540) % 360) - 180;

// A walk network is a polyline through survey points, not a list of decisions:
// most of its vertices are a metre or two of drift, not a turn.
const STRAIGHT_DEG = 20; // below this the visitor is still "going straight"
const SLIGHT_DEG = 50; // 20-50 is a slight turn, 50-125 a plain one
const SHARP_DEG = 125; // 125-160 sharp, beyond that a doubling back
const UTURN_DEG = 160;
const MIN_LEG_M = 3; // never two instructions inside three metres
const MERGE_M = 2; // a sub-2 m instruction is a wiggle: fold it forward

// The number that does the actual collapsing. A vertex's turn is measured
// between where the path came from 8 m BACK and where it goes 8 m AHEAD, not
// between the two legs that touch it.
//
// Measured, not assumed: with the per-leg comparison this file started with, a
// 100 m survey line of 60 vertices carrying ±17 cm of zig-zag produced TWENTY
// ONE instructions — "bear right 5 m, bear left 5 m" all the way down. Each leg
// was only 1.7 m, so 17 cm of drift is 11°, and two consecutive legs differ by
// 22°: over the threshold, every time. Averaging the same jitter over 8 m makes
// it 1°, while a real corner is still a real corner. The same line now yields
// one instruction.
const LOOK_M = 8;

function bucket(d) {
  const a = Math.abs(d);
  if (a < STRAIGHT_DEG) return null;
  const side = d < 0 ? "left" : "right";
  if (a >= UTURN_DEG) return "uturn";
  if (a < SLIGHT_DEG) return `slight_${side}`;
  if (a < SHARP_DEG) return side;
  return `sharp_${side}`;
}

/** Consecutive identical points (the origin snap can repeat a node, and a
 *  one-node floor segment is deliberately duplicated by build_route so it is a
 *  valid LineString) would make bearing() meaningless. */
function dedupe(coords) {
  const out = [];
  for (const c of coords) {
    const last = out[out.length - 1];
    if (!last || last[0] !== c[0] || last[1] !== c[1]) out.push(c);
  }
  return out;
}

/** The coordinate `metres` along the polyline, clamped to both ends. Distances
 *  here are tens of metres, so interpolating in degrees is exact enough. */
function pointAt(pts, cum, metres) {
  const total = cum[cum.length - 1];
  const d = Math.max(0, Math.min(total, metres));
  let i = 1;
  while (i < cum.length - 1 && cum[i] < d) i++;
  const span = cum[i] - cum[i - 1];
  const f = span > 0 ? (d - cum[i - 1]) / span : 0;
  return [
    pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * f,
    pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * f,
  ];
}

function walkSegment(coords, floorId, out) {
  const pts = dedupe(coords);
  if (pts.length < 2) return;

  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + haversineM(pts[i - 1], pts[i]));

  // Smoothed turn at every interior vertex.
  const turn = new Array(pts.length).fill(0);
  for (let i = 1; i < pts.length - 1; i++) {
    const back = pointAt(pts, cum, cum[i] - LOOK_M);
    const fwd = pointAt(pts, cum, cum[i] + LOOK_M);
    turn[i] = delta(bearingDeg(back, pts[i]), bearingDeg(pts[i], fwd));
  }

  let pending = { kind: "continue", turn: null, at: pts[0], floorId };
  let from = 0; // index the current instruction started at
  let i = 1;
  const last = pts.length - 1;

  while (i < last) {
    // Not a turn, or too soon after the previous instruction to be a separate
    // one: keep walking and let the metres accumulate.
    if (Math.abs(turn[i]) < STRAIGHT_DEG || cum[i] - cum[from] < MIN_LEG_M) {
      i++;
      continue;
    }
    // A corner is usually several vertices, not one — take the sharpest of the
    // run so a rounded corner is ONE "turn right", not three "bear right"s.
    // The run only extends over vertices that are CLOSE TOGETHER: without that
    // bound, four genuine corners 13-45 m apart merged into a single turn and
    // 80 m of "continue" (seen on the real Marina route while writing this).
    let peak = i;
    let j = i;
    while (
      j + 1 < last &&
      Math.abs(turn[j + 1]) >= STRAIGHT_DEG &&
      cum[j + 1] - cum[j] <= LOOK_M
    ) {
      j++;
      if (Math.abs(turn[j]) > Math.abs(turn[peak])) peak = j;
    }
    out.push({ ...pending, meters: cum[peak] - cum[from] });
    pending = { kind: "turn", turn: bucket(turn[peak]), at: pts[peak], floorId };
    from = peak;
    i = j + 1;
  }
  out.push({ ...pending, meters: cum[last] - cum[from] });
}

/** Fold anything under MERGE_M into the step that follows it: its metres are
 *  added there and its (tiny) turn is dropped, which is the right outcome — a
 *  1 m dogleg is not a decision the visitor has to make. */
function mergeTiny(steps) {
  const out = [];
  let carry = 0;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const isLastMove = i === steps.length - 1;
    if (!isLastMove && s.meters + carry < MERGE_M) {
      carry += s.meters;
      continue;
    }
    out.push({ ...s, meters: s.meters + carry });
    carry = 0;
  }
  if (carry && out.length) out[out.length - 1].meters += carry;
  return out;
}

const TRANSITION_KEYS = {
  elevator: "route_take_elevator",
  escalator: "route_take_escalator",
  stairs: "route_take_stairs",
  ramp: "route_take_ramp",
};

/** The catalogue is small and closed; anything else still has to say something
 *  a visitor can act on, so it falls back to a kind-free sentence rather than
 *  rendering the raw key (t() returns the key itself when it is missing). */
export const transitionKey = (kind) => TRANSITION_KEYS[kind] ?? "route_take_transition";

/**
 * @param route  the /route response, plus `poi` as the viewer attaches it
 * @param opts.destinationName already-localised POI name for the arrival step
 * @returns [{ id, kind, key, vars, meters, floorId, at, turn?, toFloorId? }]
 */
export function buildSteps(route, { destinationName = "" } = {}) {
  if (!route?.segments?.length) return [];

  const steps = [];
  route.segments.forEach((seg, i) => {
    const moves = [];
    walkSegment(seg.geometry?.coordinates ?? [], seg.floor_id, moves);
    steps.push(...mergeTiny(moves));

    const tr = seg.transition_out;
    if (tr) {
      steps.push({
        kind: "transition",
        transition: tr.kind,
        toFloorId: tr.to_floor_id,
        floorId: seg.floor_id,
        at: tr.at,
        meters: 0,
      });
    } else if (i === route.segments.length - 1) {
      steps.push({
        kind: "arrive",
        floorId: seg.floor_id,
        at: route.destination,
        meters: 0,
      });
    }
  });

  return steps.map((s, i) => ({
    ...s,
    id: `s${i}`,
    meters: Math.max(0, Math.round(s.meters)),
    ...instruction(s, destinationName),
  }));
}

function instruction(step, destinationName) {
  const m = Math.max(0, Math.round(step.meters));
  if (step.kind === "continue") return { key: "step_continue", vars: { m } };
  if (step.kind === "turn") return { key: `step_${step.turn}`, vars: { m } };
  if (step.kind === "transition")
    return { key: transitionKey(step.transition), vars: {} }; // {floor} filled by the caller
  return { key: "step_arrive", vars: { name: destinationName } };
}
